import z from 'zod'

const turnUrlsJsonSchema = z.array(z.string().min(1)).min(1)

/**
 * Parses `AUDIENCE_WEBRTC_TURN_URLS` JSON (array of turn:/turns: URL strings).
 */
export function parseAudienceWebRtcTurnUrlsJson(raw: string | undefined | null): string[] | null {
  if (raw == null || raw.trim() === '') {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    const r = turnUrlsJsonSchema.safeParse(parsed)
    return r.success ? r.data : null
  }
  catch {
    return null
  }
}

export interface IMintAudienceWebRtcTurnRestCredentialParams {
  sharedSecret: string
  /** Unix seconds at which the credential expires (coturn REST username embeds this). */
  expiryUnix: number
  /** Suffix after `expiry:` in the username (coturn “username” part). */
  usernameSuffix?: string
}

/**
 * Coturn-compatible time-limited TURN credentials (TURN REST API).
 * `username` = `${expiryUnix}:${suffix}`, `credential` = base64(HMAC-SHA1(secret, username)).
 *
 * Configure coturn with `use-auth-secret` and the same static secret.
 */
export async function mintAudienceWebRtcTurnRestCredential(
  params: IMintAudienceWebRtcTurnRestCredentialParams
): Promise<{ username: string, credential: string }> {
  const suffix = (params.usernameSuffix?.trim() !== '' && params.usernameSuffix != null)
    ? params.usernameSuffix.trim()
    : 'lucimark'
  const username = `${params.expiryUnix}:${suffix}`
  const enc = new TextEncoder()
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(params.sharedSecret),
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign']
  )
  const sig = await crypto.subtle.sign('HMAC', key, enc.encode(username))
  const bytes = new Uint8Array(sig)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!)
  }
  const credential = btoa(binary)
  return { username, credential }
}
